"use client";
import { API_ENDPOINTS } from "../../../constants/config";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "react-hot-toast";
import {
  Save,
  Plus,
  Trash,
  AlertCircle,
  Search,
  Edit,
  Bell,
  Mail,
  MessageSquare,
  Smartphone,
  Users,
  UserCheck,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  X,
} from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function AlertManagement() {
  const [notificationSettings, setNotificationSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [currentMessage, setCurrentMessage] = useState("");
  const [expandedItems, setExpandedItems] = useState({});
  const [newEvent, setNewEvent] = useState({
    event: "",
    destinations: {
      email: true,
      sms: false,
      mobileApp: false,
    },
    recipients: {
      studentParent: true,
      teacher: true,
    },
    sampleMessage: "",
  });
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        API_ENDPOINTS.notificationsSettings,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Transform any existing data to use studentParent instead of student and teacher instead of guardian
      const transformedData = (response.data.data || []).map((item) => {
        return {
          ...item,
          recipients: {
            studentParent: item.recipients?.student || false,
            teacher: item.recipients?.guardian || false,
          },
        };
      });

      setNotificationSettings(transformedData);
      setLoading(false);
    } catch (err) {
      setError("Failed to load notification settings");
      setLoading(false);
      console.error("Error fetching notification settings:", err);
      setNotificationSettings([]);
    }
  };

  const handleCheckboxChange = (index, field, subField) => {
    const updatedSettings = [...notificationSettings];
    updatedSettings[index][field][subField] =
      !updatedSettings[index][field][subField];
    setNotificationSettings(updatedSettings);
    setHasUnsavedChanges(true);
  };

  const handleNewEventCheckboxChange = (field, subField) => {
    setNewEvent({
      ...newEvent,
      [field]: {
        ...newEvent[field],
        [subField]: !newEvent[field][subField],
      },
    });
    setHasUnsavedChanges(true);
  };

  const handleInputChange = (index, field, value) => {
    const updatedSettings = [...notificationSettings];
    updatedSettings[index][field] = value;
    setNotificationSettings(updatedSettings);
    setHasUnsavedChanges(true);
  };

  const handleNewEventInputChange = (field, value) => {
    setNewEvent({
      ...newEvent,
      [field]: value,
    });
    setHasUnsavedChanges(true);
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      await axiosInstance.post(API_ENDPOINTS.notificationsSettings, {
        settings: notificationSettings,
      });
      toast.success("Notification settings saved successfully");
      setLoading(false);
      setEditMode(false);
      setHasUnsavedChanges(false);
    } catch (err) {
      toast.error("Failed to save notification settings");
      setLoading(false);
      console.error("Error saving notification settings:", err);
    }
  };

  const addNewEvent = () => {
    if (!newEvent.event) {
      toast.error("Event name is required");
      return;
    }

    setNotificationSettings([...notificationSettings, newEvent]);
    setNewEvent({
      event: "",
      destinations: {
        email: true,
        sms: false,
        mobileApp: false,
      },
      recipients: {
        studentParent: true,
        teacher: true,
      },
      sampleMessage: "",
    });
  };

  const deleteEvent = (index) => {
    const updatedSettings = [...notificationSettings];
    updatedSettings.splice(index, 1);
    setNotificationSettings(updatedSettings);
    setHasUnsavedChanges(true);
  };

  const openMessageDialog = (index) => {
    setCurrentEditIndex(index);
    setCurrentMessage(notificationSettings[index].sampleMessage);
    setDialogOpen(true);
  };

  const saveMessageTemplate = () => {
    const updatedSettings = [...notificationSettings];
    updatedSettings[currentEditIndex].sampleMessage = currentMessage;
    setNotificationSettings(updatedSettings);
    setDialogOpen(false);
    toast.success("Message template updated");
    setHasUnsavedChanges(true);
  };

  const toggleExpand = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Filter settings based on search term and active tab
  const filteredSettings = notificationSettings.filter((setting) => {
    const matchesSearch =
      setting.event.toLowerCase().includes(searchTerm.toLowerCase()) ||
      setting.sampleMessage.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeTab === "all") return matchesSearch;
    if (activeTab === "email")
      return matchesSearch && setting.destinations.email;
    if (activeTab === "sms") return matchesSearch && setting.destinations.sms;
    if (activeTab === "app")
      return matchesSearch && setting.destinations.mobileApp;

    return matchesSearch;
  });

  if (loading && notificationSettings.length === 0) {
    return (
      <div className="flex justify-center items-center p-16 h-64">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary rounded-full animate-spin mb-4"></div>
          <div className="text-lg font-medium">
            Loading notification settings...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 rounded-lg border border-destructive/20 bg-destructive/5">
        <div className="flex items-center gap-3 text-destructive mb-2">
          <AlertCircle className="h-5 w-5" />
          <h3 className="font-semibold">Error Loading Settings</h3>
        </div>
        <p>{error}</p>
        <Button
          variant="outline"
          className="mt-4 border-destructive/50 text-destructive hover:bg-destructive/10"
          onClick={fetchNotificationSettings}
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between bg-background border-b">
        <div>
          <CardTitle className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-primary" />
            Alert Management
          </CardTitle>
          <CardDescription>
            Configure notification settings for different events
          </CardDescription>
        </div>
        <div className="flex gap-2">
          {hasUnsavedChanges && (
            <Button onClick={saveSettings} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Save All Changes
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 bg-muted/30 border-b">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search events or messages..."
              className="pl-10 bg-background"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <Tabs
          defaultValue="all"
          className="w-full"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <div className="px-4 bg-background">
            <TabsList className="grid grid-cols-4 gap-4 w-full max-w-md mx-auto my-4">
              <TabsTrigger value="all" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">All</TabsTrigger>
              <TabsTrigger value="email" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">
                <Mail className="h-4 w-4 mr-1" />
                Email
              </TabsTrigger>
              <TabsTrigger value="sms" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">
                <MessageSquare className="h-4 w-4 mr-1" />
                SMS
              </TabsTrigger>
              <TabsTrigger value="app" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">
                <Smartphone className="h-4 w-4 mr-1" />
                App
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="all" className="mt-0">
            <div className="p-4">
              {filteredSettings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground bg-background rounded-lg border">
                  <AlertCircle className="h-12 w-12 mb-3 text-muted-foreground" />
                  {searchTerm ? (
                    <p className="text-lg font-medium">
                      No events match your search
                    </p>
                  ) : (
                    <p className="text-lg font-medium">
                      No notification events configured yet
                    </p>
                  )}
                  {editMode && (
                    <Button
                      onClick={() =>
                        document
                          .getElementById("new-event-form")
                          .scrollIntoView({ behavior: "smooth" })
                      }
                      className="mt-4"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Your First Event
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredSettings.map((setting, index) => {
                    const originalIndex = notificationSettings.findIndex(
                      (s) => s === setting
                    );
                    const isExpanded = expandedItems[originalIndex];

                    return (
                      <div
                        key={index}
                        className="bg-background rounded-lg shadow-sm overflow-hidden border"
                      >
                        <div
                          className={`flex items-center justify-between p-4 cursor-pointer ${
                            isExpanded ? "bg-muted/20 border-b" : ""
                          }`}
                          onClick={() => toggleExpand(originalIndex)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="bg-primary/10 text-primary p-2 rounded-full">
                              <Bell className="h-5 w-5" />
                            </div>
                            <div>
                              {editMode && isExpanded ? (
                                <Input
                                  value={setting.event}
                                  onChange={(e) =>
                                    handleInputChange(
                                      originalIndex,
                                      "event",
                                      e.target.value
                                    )
                                  }
                                  className="font-medium"
                                />
                              ) : (
                                <h3 className="font-medium">{setting.event}</h3>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              {setting.destinations.email && (
                                <Badge
                                  variant="outline"
                                  className="bg-background"
                                >
                                  <Mail className="h-3 w-3 mr-1" /> Email
                                </Badge>
                              )}
                              {setting.destinations.sms && (
                                <Badge
                                  variant="outline"
                                  className="bg-background"
                                >
                                  <MessageSquare className="h-3 w-3 mr-1" /> SMS
                                </Badge>
                              )}
                              {setting.destinations.mobileApp && (
                                <Badge
                                  variant="outline"
                                  className="bg-background"
                                >
                                  <Smartphone className="h-3 w-3 mr-1" /> App
                                </Badge>
                              )}
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="p-4 bg-background">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <Users className="h-4 w-4 text-primary" />{" "}
                                  Recipients
                                </h4>
                                <div className="space-y-3 bg-muted/10 p-3 rounded-lg">
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`studentParent-${originalIndex}`}
                                      checked={setting.recipients.studentParent}
                                      disabled={!editMode}
                                      onCheckedChange={() =>
                                        handleCheckboxChange(
                                          originalIndex,
                                          "recipients",
                                          "studentParent"
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`studentParent-${originalIndex}`}
                                      className="flex items-center gap-1"
                                    >
                                      <UserCheck className="h-4 w-4 text-primary" />{" "}
                                      Student/Parent
                                    </label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`teacher-${originalIndex}`}
                                      checked={setting.recipients.teacher}
                                      disabled={!editMode}
                                      onCheckedChange={() =>
                                        handleCheckboxChange(
                                          originalIndex,
                                          "recipients",
                                          "teacher"
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`teacher-${originalIndex}`}
                                      className="flex items-center gap-1"
                                    >
                                      <Users className="h-4 w-4 text-primary" />{" "}
                                      Teacher
                                    </label>
                                  </div>
                                </div>

                                <h4 className="font-medium mt-4 mb-3">
                                  Delivery Methods
                                </h4>
                                <div className="space-y-3 bg-muted/10 p-3 rounded-lg">
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`email-${originalIndex}`}
                                      checked={setting.destinations.email}
                                      disabled={!editMode}
                                      onCheckedChange={() =>
                                        handleCheckboxChange(
                                          originalIndex,
                                          "destinations",
                                          "email"
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`email-${originalIndex}`}
                                      className="flex items-center gap-1"
                                    >
                                      <Mail className="h-4 w-4 text-primary" />{" "}
                                      Email
                                    </label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`sms-${originalIndex}`}
                                      checked={setting.destinations.sms}
                                      disabled={!editMode}
                                      onCheckedChange={() =>
                                        handleCheckboxChange(
                                          originalIndex,
                                          "destinations",
                                          "sms"
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`sms-${originalIndex}`}
                                      className="flex items-center gap-1"
                                    >
                                      <MessageSquare className="h-4 w-4 text-primary" />{" "}
                                      SMS
                                    </label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`mobile-${originalIndex}`}
                                      checked={setting.destinations.mobileApp}
                                      disabled={!editMode}
                                      onCheckedChange={() =>
                                        handleCheckboxChange(
                                          originalIndex,
                                          "destinations",
                                          "mobileApp"
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`mobile-${originalIndex}`}
                                      className="flex items-center gap-1"
                                    >
                                      <Smartphone className="h-4 w-4 text-primary" />{" "}
                                      Mobile App
                                    </label>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-medium flex items-center gap-2">
                                    <MessageSquare className="h-4 w-4 text-primary" />{" "}
                                    Message Template
                                  </h4>
                                  {editMode && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-8"
                                      onClick={() =>
                                        openMessageDialog(originalIndex)
                                      }
                                    >
                                      <Edit className="h-3 w-3 mr-1" /> Edit
                                      Template
                                    </Button>
                                  )}
                                </div>
                                <div className="bg-muted/10 p-4 rounded-lg border min-h-[120px]">
                                  {setting.sampleMessage ? (
                                    <p className="whitespace-pre-wrap">
                                      {setting.sampleMessage}
                                    </p>
                                  ) : (
                                    <p className="text-muted-foreground italic">
                                      No message template provided
                                    </p>
                                  )}
                                </div>

                                <div className="mt-4 flex justify-between">
                                  {deleteConfirmIndex === originalIndex ? (
                                    <div className="flex gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          setDeleteConfirmIndex(null)
                                        }
                                      >
                                        <X className="h-4 w-4 mr-1" /> Cancel
                                      </Button>
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() =>
                                          deleteEvent(originalIndex)
                                        }
                                      >
                                        <Trash className="h-4 w-4 mr-1" />{" "}
                                        Confirm Delete
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() =>
                                        setDeleteConfirmIndex(originalIndex)
                                      }
                                    >
                                      <Trash className="h-4 w-4 mr-1" /> Delete
                                      Event
                                    </Button>
                                  )}

                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const updatedSettings = [
                                          ...notificationSettings,
                                        ];
                                        const updatedSetting = {
                                          ...updatedSettings[originalIndex],
                                        };
                                        updatedSettings[originalIndex] =
                                          updatedSetting;
                                        setNotificationSettings(
                                          updatedSettings
                                        );
                                        setHasUnsavedChanges(true);
                                        saveSettings();
                                      }}
                                    >
                                      <Save className="h-4 w-4 mr-1" /> Save
                                      Changes
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setEditMode(!editMode)}
                                    >
                                      <Edit className="h-4 w-4 mr-1" /> Edit
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {editMode && (
                <div
                  id="new-event-form"
                  className="mt-6 bg-background rounded-lg shadow-sm p-4 border"
                >
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Plus className="h-5 w-5 text-primary" /> Add New
                    Notification Event
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">
                          Event Name
                        </label>
                        <Input
                          placeholder="Enter event name"
                          value={newEvent.event}
                          onChange={(e) =>
                            handleNewEventInputChange("event", e.target.value)
                          }
                        />
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2">Recipients</h4>
                        <div className="space-y-3 bg-muted/10 p-3 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="new-studentParent"
                              checked={newEvent.recipients.studentParent}
                              onCheckedChange={() =>
                                handleNewEventCheckboxChange(
                                  "recipients",
                                  "studentParent"
                                )
                              }
                            />
                            <label
                              htmlFor="new-studentParent"
                              className="flex items-center gap-1"
                            >
                              <UserCheck className="h-4 w-4 text-primary" />{" "}
                              Student/Parent
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="new-teacher"
                              checked={newEvent.recipients.teacher}
                              onCheckedChange={() =>
                                handleNewEventCheckboxChange(
                                  "recipients",
                                  "teacher"
                                )
                              }
                            />
                            <label
                              htmlFor="new-teacher"
                              className="flex items-center gap-1"
                            >
                              <Users className="h-4 w-4 text-primary" /> Teacher
                            </label>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-medium mb-2">
                          Delivery Methods
                        </h4>
                        <div className="space-y-3 bg-muted/10 p-3 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="new-email"
                              checked={newEvent.destinations.email}
                              onCheckedChange={() =>
                                handleNewEventCheckboxChange(
                                  "destinations",
                                  "email"
                                )
                              }
                            />
                            <label
                              htmlFor="new-email"
                              className="flex items-center gap-1"
                            >
                              <Mail className="h-4 w-4 text-primary" /> Email
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="new-sms"
                              checked={newEvent.destinations.sms}
                              onCheckedChange={() =>
                                handleNewEventCheckboxChange(
                                  "destinations",
                                  "sms"
                                )
                              }
                            />
                            <label
                              htmlFor="new-sms"
                              className="flex items-center gap-1"
                            >
                              <MessageSquare className="h-4 w-4 text-primary" />{" "}
                              SMS
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="new-mobile"
                              checked={newEvent.destinations.mobileApp}
                              onCheckedChange={() =>
                                handleNewEventCheckboxChange(
                                  "destinations",
                                  "mobileApp"
                                )
                              }
                            />
                            <label
                              htmlFor="new-mobile"
                              className="flex items-center gap-1"
                            >
                              <Smartphone className="h-4 w-4 text-primary" />{" "}
                              Mobile App
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">
                          Message Template
                        </label>
                        <Textarea
                          placeholder="Enter message template with variables like {studentName}"
                          value={newEvent.sampleMessage}
                          onChange={(e) =>
                            handleNewEventInputChange(
                              "sampleMessage",
                              e.target.value
                            )
                          }
                          rows={6}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          You can use variables: {"{studentName}"},{" "}
                          {"{teacherName}"}, {"{eventDate}"}, {"{eventTime}"}
                        </p>
                      </div>

                      <div className="flex justify-end mt-4">
                        <Button onClick={addNewEvent}>
                          <Plus className="h-4 w-4 mr-1" /> Add Event
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="email" className="mt-0">
            <div className="p-4">
              <div className="bg-primary/5 border rounded-lg p-4 mb-4 flex items-start gap-3">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium">Email Notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    Showing all events configured to send email notifications
                  </p>
                </div>
              </div>
              {/* Content is the same as "all" tab but filtered for email */}
              {filteredSettings.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground bg-background rounded-lg border">
                  <AlertCircle className="h-12 w-12 mb-3 text-muted-foreground" />
                  <p className="text-lg font-medium">
                    No email notifications configured
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sms" className="mt-0">
            <div className="p-4">
              <div className="bg-primary/5 border rounded-lg p-4 mb-4 flex items-start gap-3">
                <MessageSquare className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium">SMS Notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    Showing all events configured to send SMS notifications
                  </p>
                </div>
              </div>
              {/* Content is the same as "all" tab but filtered for SMS */}
              {filteredSettings.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground bg-background rounded-lg border">
                  <AlertCircle className="h-12 w-12 mb-3 text-muted-foreground" />
                  <p className="text-lg font-medium">
                    No SMS notifications configured
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="app" className="mt-0">
            <div className="p-4">
              <div className="bg-primary/5 border rounded-lg p-4 mb-4 flex items-start gap-3">
                <Smartphone className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <h3 className="font-medium">Mobile App Notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    Showing all events configured to send mobile app
                    notifications
                  </p>
                </div>
              </div>
              {/* Content is the same as "all" tab but filtered for mobile app */}
              {filteredSettings.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground bg-background rounded-lg border">
                  <AlertCircle className="h-12 w-12 mb-3 text-muted-foreground" />
                  <p className="text-lg font-medium">
                    No mobile app notifications configured
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Message Template Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md bg-background">
            <DialogHeader>
              <DialogTitle className="text-xl">
                Edit Message Template
              </DialogTitle>
              <DialogDescription>
                Update the message template for this notification.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-primary/5 border rounded-lg p-3 text-sm">
              <p className="font-medium">Available variables:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>{"{studentName}"} - Student's full name</li>
                <li>{"{teacherName}"} - Teacher's full name</li>
                <li>{"{eventDate}"} - Date of the event</li>
                <li>{"{eventTime}"} - Time of the event</li>
              </ul>
            </div>
            <div className="py-4">
              <Textarea
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="Enter message template with available variables"
                rows={6}
                className="w-full"
              />
              <div className="mt-4 p-3 bg-muted/10 rounded-md border">
                <h4 className="text-sm font-medium mb-2">Preview:</h4>
                <p className="text-sm">
                  {currentMessage
                    ? currentMessage
                        .replace(/{{\s*studentName\s*}}/g, "John Doe")
                        .replace(/{{\s*teacherName\s*}}/g, "Jane Smith")
                        .replace(
                          /{{\s*eventDate\s*}}/g,
                          new Date().toLocaleDateString()
                        )
                        .replace(
                          /{{\s*eventTime\s*}}/g,
                          new Date().toLocaleTimeString()
                        )
                    : "Preview will appear here..."}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button onClick={saveMessageTemplate}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Save Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmIndex !== null}
          onOpenChange={() => setDeleteConfirmIndex(null)}
        >
          <DialogContent className="sm:max-w-md bg-background">
            <DialogHeader>
              <DialogTitle className="text-destructive text-xl">
                Confirm Deletion
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this notification event? This
                action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 my-2 text-destructive">
              <AlertCircle className="h-5 w-5 mb-2" />
              <p className="text-sm">
                Deleting this event will remove all associated notification
                settings.
              </p>
            </div>
            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmIndex(null)}
              >
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (deleteConfirmIndex !== null) {
                    deleteEvent(deleteConfirmIndex);
                    setDeleteConfirmIndex(null);
                  }
                }}
              >
                <Trash className="h-4 w-4 mr-1" /> Delete Event
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default AlertManagement;
